import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * WINDOWED ROWS (0.1.474, item 3 of the perf audit) — the DOM holds the rows NEAR the viewport,
 * not the catalogue.
 *
 * WHY. The Items list renders every row of the projection: 12.7k DOM nodes on the phone and 34k
 * on the desk at 1,940 items, 2.2× that on the atelier's real ~4,200. 0.1.471's `content-visibility`
 * spared the browser the layout and paint of off-screen rows, and the probe showed what it could
 * not spare: React's COMMIT of the nodes themselves (110–500 ms on a desktop Chrome, several
 * times that on a phone). The only way to not pay for a node is to not create it.
 *
 * HOW. A leaf group's rows are cut into BANDS of fifty. Each band is one element that is always in
 * the DOM: near the viewport it contains its rows, far from it it is an empty spacer of the
 * band's height — so the scrollbar, the sticky rails and scroll restoration all see the same
 * geometry either way. One shared IntersectionObserver with a screen and a half of margin flips
 * bands as the person scrolls; a band that scrolls far away gives its rows back.
 *
 * ⚠️ EVERY BAND STARTS AS A SPACER, the on-screen ones fill in on the observer's first report a
 * frame later. The first cut rendered each leaf's first band at once "to save a frame" — and
 * since every leaf group has its own first band, that was nearly every row rendered, measured
 * (a forced layout per leaf, 180 ms of `getBoundingClientRect` in the profile), and then torn
 * out again when the observer said "far". Slower than no windowing at all, by 200–400 ms. One
 * frame of spacers is the honest price of not knowing where the viewport is before layout.
 *
 * THE ROW HEIGHT a spacer claims is measured ONCE per list class, from the first band that renders,
 * in a `requestAnimationFrame` after the commit — the browser has laid the page out by then, so the
 * read forces nothing. The caller's `rowHeight` is the estimate until that frame.
 *
 * WHAT IT DOES NOT TOUCH. Rails, folds, the tree, the drawn-order `data-row-index` (a band renders
 * the same rows with the same indices the flat render would) — the shared selection gestures work
 * unchanged, and a drag over a spacer simply finds no row (select-box.tsx already tolerates that).
 * `windowed={false}` renders exactly what the list rendered before this file existed, which is
 * what printing asks for (every group open, every row present) and what the smaller lists keep.
 * Ctrl+F cannot find a row inside a spacer; the search box is the door for that, as it always was
 * for a collapsed group.
 */

/** Rows per band. Fifty is a screen and a bit on a phone: a flip materialises about one screen. */
export const BAND_SIZE = 50;

/**
 * THE LIST SIZE FROM WHICH WINDOWING IS WORTH IT (0.1.476). Below it the whole list commits in a
 * few milliseconds, and windowing's price — a frame of spacers, rows outside a screen and a half
 * absent from the DOM — buys nothing. The FULL gate found the second half of that price: a phone
 * layout spec at 375×360 asserted a seeded row that an earlier spec had moved into a later status
 * group, two screens down, and windowing had (correctly) left it a spacer. Two hundred rows is
 * ~1,300 nodes on the phone list: nothing to save. The atelier's ~4,200 is where it matters.
 */
export const WINDOW_FROM_ROWS = 200;

type Listener = (near: boolean) => void;
const listeners = new WeakMap<Element, Listener>();
let shared: IntersectionObserver | null = null;
function sharedObserver(): IntersectionObserver {
  shared ??= new IntersectionObserver(
    (entries) => {
      for (const entry of entries) listeners.get(entry.target)?.(entry.isIntersecting);
    },
    { rootMargin: '150% 0px' },
  );
  return shared;
}

/** Measured row height per list class (`gl-band`, `ds-band`) — one number for the whole list. */
const measuredRowHeight = new Map<string, number>();

export function WindowedRows<Row>({
  rows,
  render,
  rowHeight,
  windowed,
  className,
}: {
  rows: Row[];
  /** Renders a SLICE of rows — the caller's own row markup, unchanged. */
  render: (slice: Row[]) => ReactNode;
  /** The estimate a spacer uses until a band has been measured, in px. */
  rowHeight: number;
  /** Off = render every row with no wrappers at all (print, the small lists). */
  windowed: boolean;
  /** The band element's class — the list's own spacer styling, and the measurement's key. */
  className: string;
}): JSX.Element {
  if (!windowed) return <>{render(rows)}</>;
  const bands: Row[][] = [];
  for (let i = 0; i < rows.length; i += BAND_SIZE) bands.push(rows.slice(i, i + BAND_SIZE));
  return (
    <>
      {bands.map((band, i) => (
        <Band key={i} count={band.length} estimate={rowHeight} className={className}>
          {() => render(band)}
        </Band>
      ))}
    </>
  );
}

function Band({
  count,
  estimate,
  className,
  children,
}: {
  count: number;
  estimate: number;
  className: string;
  children: () => ReactNode;
}): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const [near, setNear] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (el === null) return;
    // No observer (an old browser, jsdom): render everything, the way the list always did.
    if (typeof IntersectionObserver === 'undefined') {
      setNear(true);
      return;
    }
    listeners.set(el, setNear);
    sharedObserver().observe(el);
    return () => {
      sharedObserver().unobserve(el);
      listeners.delete(el);
    };
  }, []);
  // Measure a rendered band once per list class, after the frame — never a forced layout.
  useEffect(() => {
    if (!near || count === 0 || measuredRowHeight.has(className)) return;
    const frame = requestAnimationFrame(() => {
      const el = ref.current;
      if (el === null || measuredRowHeight.has(className)) return;
      const h = el.getBoundingClientRect().height;
      if (h > 0) measuredRowHeight.set(className, h / count);
    });
    return () => cancelAnimationFrame(frame);
  }, [near, count, className]);
  const height = near ? undefined : `${String(Math.round(count * (measuredRowHeight.get(className) ?? estimate)))}px`;
  return (
    <div ref={ref} className={className} style={height === undefined ? undefined : { height }} role="presentation" aria-hidden={near ? undefined : true}>
      {near ? children() : null}
    </div>
  );
}
